import { jsPDF } from 'jspdf';
import { CourseCertificate } from '../types/learning.types';
import { toSydneyDate } from './helpers';

/** Served from `client/public` (same convention as /proview_scaffolding_our_values.pdf). */
const PROVIEW_LOGO_PATH = '/proview_scaffolding_logo.jpg';
/** Source image is 1203x378; used to place the mark without distorting it. */
const PROVIEW_LOGO_ASPECT = 378 / 1203;

/**
 * The mark needs to reach jsPDF as a data URL, and `addImage` is synchronous, so the
 * certificate awaits this before it draws. Cached after the first success; a failure clears
 * the cache so a later download can retry rather than staying broken for the session.
 */
let logoPromise: Promise<string | null> | null = null;

const loadProviewLogo = (): Promise<string | null> => {
    if (!logoPromise) {
        logoPromise = (async () => {
            const response = await fetch(PROVIEW_LOGO_PATH);
            if (!response.ok) throw new Error(`logo ${response.status}`);
            const blob = await response.blob();
            return await new Promise<string>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result));
                reader.onerror = () => reject(reader.error);
                reader.readAsDataURL(blob);
            });
        })().catch(() => {
            // The mark is decoration: a certificate without it is still a valid certificate,
            // so never block the download on a missing asset.
            logoPromise = null;
            return null;
        });
    }
    return logoPromise;
};

/**
 * Learning certificate PDF (LEARNING.md §13.3).
 *
 * Rendered CLIENT-SIDE with the already-installed jspdf. The server owns the certificate
 * RECORD (number, issue date, expiry) and this function is a pure presentation of it — so
 * the PDF is reproducible from the record at any time and no generated files accumulate
 * in S3.
 *
 * Drawn directly with vector text rather than a canvas capture: a certificate is text on
 * a page, so text stays crisp and the file stays a few KB.
 *
 * The only raster asset is the Proview mark above the signature rule, fetched from
 * `public/` on first use — hence the async signature.
 *
 * @returns A promise that settles once the file has been handed to the browser. Resolves
 *          even if the mark could not be loaded (see `loadProviewLogo`).
 */
export const generateLearningCertificatePdf = async (
    certificate: CourseCertificate,
    recipientName: string,
): Promise<void> => {
    const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });

    const pageWidth = 297;
    const pageHeight = 210;
    const centre = pageWidth / 2;

    // Frame: heavy outer rule, thin inner rule.
    doc.setDrawColor(3, 2, 19);
    doc.setLineWidth(1.2);
    doc.rect(12, 12, pageWidth - 24, pageHeight - 24);
    doc.setLineWidth(0.3);
    doc.rect(15, 15, pageWidth - 30, pageHeight - 30);

    // Heading.
    doc.setTextColor(113, 113, 130);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.text('CERTIFICATE OF COMPLETION', centre, 46, { align: 'center' });

    // Recipient.
    doc.setTextColor(3, 2, 19);
    doc.setFont('times', 'bold');
    doc.setFontSize(30);
    doc.text(recipientName || 'Learner', centre, 74, { align: 'center' });

    doc.setDrawColor(3, 2, 19);
    doc.setLineWidth(0.4);
    doc.line(centre - 55, 81, centre + 55, 81);

    // Course.
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(113, 113, 130);
    doc.text('has successfully completed', centre, 94, { align: 'center' });

    doc.setFont('times', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(3, 2, 19);
    doc.text(certificate.courseTitle || '', centre, 108, { align: 'center', maxWidth: pageWidth - 80 });

    // Footer: number, issued, expiry.
    const footerY = 160;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(55, 65, 81);

    doc.text(`Certificate number: ${certificate.certificateNumber}`, 30, footerY);
    doc.text(`Issued: ${certificate.issuedDate ? toSydneyDate(certificate.issuedDate) : '–'}`, 30, footerY + 7);
    doc.text(
        certificate.expiryDate
            ? `Expires: ${toSydneyDate(certificate.expiryDate)}`
            : 'Expires: No expiry',
        30,
        footerY + 14,
    );

    // Signature block: the Proview mark sits above the rule, the caption below it.
    const signatureRuleY = footerY + 6;
    const signatureCentre = pageWidth - 65;

    const logo = await loadProviewLogo();
    if (logo) {
        const logoWidth = 48;
        const logoHeight = logoWidth * PROVIEW_LOGO_ASPECT;
        doc.addImage(
            logo,
            'JPEG',
            signatureCentre - logoWidth / 2,
            signatureRuleY - logoHeight - 2,
            logoWidth,
            logoHeight,
        );
    }

    doc.setDrawColor(156, 163, 175);
    doc.setLineWidth(0.3);
    doc.line(pageWidth - 100, signatureRuleY, pageWidth - 30, signatureRuleY);
    doc.setTextColor(113, 113, 130);
    doc.setFontSize(9);
    doc.text('Authorised by', signatureCentre, signatureRuleY + 5, { align: 'center' });

    doc.save(`certificate-${certificate.certificateNumber}.pdf`);
};
